import * as React from 'react';
import type { AuthLoginResponse } from '@aggregator/shared';
import { api, getToken, setToken, setUnauthorizedHandler } from '@/api/client';

interface AuthContextValue {
  isAuthenticated: boolean;
  username: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

function decodeUsername(token: string | null): string | null {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1] ?? '')) as { username?: string };
    return payload.username ?? null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setTokenState] = React.useState<string | null>(() => getToken());
  const username = React.useMemo(() => decodeUsername(token), [token]);

  React.useEffect(() => {
    setUnauthorizedHandler(() => setTokenState(null));
  }, []);

  const login = React.useCallback(async (user: string, password: string) => {
    const res = await api.post<AuthLoginResponse>('/auth/login', { username: user, password });
    setToken(res.data.token);
    setTokenState(res.data.token);
  }, []);

  const logout = React.useCallback(() => {
    setToken(null);
    setTokenState(null);
  }, []);

  const value = React.useMemo<AuthContextValue>(
    () => ({ isAuthenticated: Boolean(token), username, login, logout }),
    [token, username, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
